import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { getProgressColumns } from "./ProgressSummaryReporting";

const ProgressSummaryReportingDetails = ({ canUpdate, progressData, loading, title = "DETAILED SUMMARY BY PRIORITY AND AREA", materialType }) => {
  // Format numbers with comma as decimal separator and limit to one decimal place
  const formatNumber = (number) => {
    if (number === undefined || number === null) {
      return "0,0";
    }
    
    // Convert to number to handle any string inputs
    const num = Number(number);
    
    // Format with one decimal place and replace '.' with ','
    const formatted = num.toFixed(1);
    return formatted.replace('.', ',');
  };
  
  // Format percentage values with comma as decimal separator
  const formatPercentage = (value) => {
    if (value === undefined || value === null) {
      return "0,00 %";
    }
    
    // Convert to number to handle any string inputs
    const num = Number(value);
    
    // For whole numbers with 3 digits or less (like 100), add ',00'
    if (Number.isInteger(num) && num.toString().length <= 3) {
      return `${num},00 %`;
    } else {
      // For decimal numbers, keep two decimal places and replace '.' with ','
      const formatted = num.toFixed(2);
      // Replace the decimal point with comma
      return `${formatted.replace('.', ',')} %`;
    }
  };
  const [editingKey, setEditingKey] = useState("");
  const [data, setData] = useState([]);
  const [form] = Form.useForm();


  const [priorities, setPriorities] = useState([]);
  const [totals, setTotals] = useState(null);
  const [uom, setUom] = useState("");
  
  // Calculate totals for all columns from the formatted data
  const calculateTotals = (data) => {
    if (!data || data.length === 0) return null;
    
    const totals = {
      grossTotal: 0,
      fabrication: { totalGross: 0, percentageCompleted: 0 },
      qcRelease: { totalGross: 0, percentageCompleted: 0 },
      corrisProtDelWrkshp: { totalGross: 0, percentageCompleted: 0 },
      delSite: { totalGross: 0, percentageCompleted: 0 },
      siteReceipt: { totalGross: 0, percentageCompleted: 0 },
      installation: { totalGross:0, percentageCompleted: 0 }
    };
    
    // Sum up the quantities
    data.forEach(item => {
      totals.grossTotal += Number(item.grossTotal) || 0;
      totals.fabrication.totalGross += Number(item.fabrication?.totalGross) || 0;
      totals.qcRelease.totalGross += Number(item.qcRelease?.totalGross) || 0;
      totals.corrisProtDelWrkshp.totalGross += Number(item.corrisProtDelWrkshp?.totalGross) || 0;
      totals.delSite.totalGross += Number(item.delSite?.totalGross) || 0;
      totals.siteReceipt.totalGross += Number(item.siteReceipt?.totalGross) || 0;
      totals.installation.totalGross += Number(item.installation?.totalGross) || 0;
    });
    
    // Calculate percentages as average of all individual percentages
    const count = data.length;
    if (count > 0) {
      let fabricationPercentageSum = 0;
      let qcReleasePercentageSum = 0;
      let corrisProtDelWrkshpPercentageSum = 0;
      let delSitePercentageSum = 0;
      let siteReceiptPercentageSum = 0;
      let installationPercentageSum = 0;
      
      data.forEach(item => {
        fabricationPercentageSum += Number(item.fabrication?.percentageCompleted) || 0;
        qcReleasePercentageSum += Number(item.qcRelease?.percentageCompleted) || 0;
        corrisProtDelWrkshpPercentageSum += Number(item.corrisProtDelWrkshp?.percentageCompleted) || 0;
        delSitePercentageSum += Number(item.delSite?.percentageCompleted) || 0;
        siteReceiptPercentageSum += Number(item.siteReceipt?.percentageCompleted) || 0;
        installationPercentageSum += Number(item.installation?.percentageCompleted) || 0;
      });
      
      totals.fabrication.percentageCompleted = fabricationPercentageSum / count;
      totals.qcRelease.percentageCompleted = qcReleasePercentageSum / count;
      totals.corrisProtDelWrkshp.percentageCompleted = corrisProtDelWrkshpPercentageSum / count;
      totals.delSite.percentageCompleted = delSitePercentageSum / count;
      totals.siteReceipt.percentageCompleted = siteReceiptPercentageSum / count;
      totals.installation.percentageCompleted = installationPercentageSum / count;
    }
    
    return totals;
  };
  
  useEffect(() => {
    if (progressData) {
      const extractedPriorities = extractPriorities(progressData);
      setPriorities(extractedPriorities);
      
      // Extract UOM from the first material type
      const firstMaterialType = Object.keys(progressData)[0];
      if (firstMaterialType && progressData[firstMaterialType]) {
        setUom((progressData[firstMaterialType].uom || "").toLowerCase());
      }
      
      const formattedData = formatProgressData(progressData);
      setData(formattedData);
      
      // Calculate totals from the formatted data
      const calculatedTotals = calculateTotals(formattedData);
      setTotals(calculatedTotals);
    }
  }, [progressData]);
  
  // Helper function to compare priorities for sorting
  const comparePriorities = (a, b) => {
    // Extract numeric part if priority starts with P followed by a number
    const getNumericPriority = (priority) => {
      const match = priority.match(/^P(\d+)$/i);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const numA = getNumericPriority(a);
    const numB = getNumericPriority(b);
    
    // If both are P-format priorities (P1, P2, etc.), compare numerically
    if (numA !== null && numB !== null) {
      return numA - numB;
    }
    
    // If only one is P-format, prioritize it
    if (numA !== null) return -1;
    if (numB !== null) return 1;
    
    // For priorities like "Priority 100", extract the number
    const getPriorityNumber = (priority) => {
      const match = priority.match(/Priority\s+(\d+)/i);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const prioNumA = getPriorityNumber(a);
    const prioNumB = getPriorityNumber(b);
    
    // If both are "Priority X" format, compare numerically
    if (prioNumA !== null && prioNumB !== null) {
      return prioNumA - prioNumB;
    }
    
    // If only one is "Priority X" format, prioritize it
    if (prioNumA !== null) return -1;
    if (prioNumB !== null) return 1;
    
    // Default to alphabetical comparison
    return a.localeCompare(b);
  };
  
  // Extract material type + priority combinations for rendering headers above the table
  const extractPriorities = (data) => {
    const materialPrioritiesMap = {};
    
    // Iterate through each material type
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      
      // Skip the 'uom' property as it's not a priority
      const priorityKeys = Object.keys(materialData).filter(key => key !== 'uom');
      
      // Sort priorities for this material type
      priorityKeys.sort(comparePriorities);
      
      // Get all priorities for this material type
      priorityKeys.forEach(priority => {
        const combinedKey = `${materialType.toUpperCase()} ${priority.toUpperCase()}`;
        materialPrioritiesMap[combinedKey] = { materialType, priority };
      });
    });
    
    // Convert to array and sort by material type first, then by priority
    return Object.keys(materialPrioritiesMap).sort((a, b) => {
      const itemA = materialPrioritiesMap[a];
      const itemB = materialPrioritiesMap[b];
      
      // First sort by material type
      if (itemA.materialType !== itemB.materialType) {
        return itemA.materialType.localeCompare(itemB.materialType);
      }
      
      // Then sort by priority
      return comparePriorities(itemA.priority, itemB.priority);
    });
  };

  // Group data by material and priority
  const groupDataByMaterialAndPriority = (data) => {
    const groupedData = {};
    
    data.forEach(item => {
      const key = item.materialPriority;
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(item);
    });
    
    return groupedData;
  };
  
  // Transform API data into table-compatible format for the nested priority and area structure
  const formatProgressData = (data) => {
    const formattedData = [];
    let position = 0;
    
    // Iterate through each material type (Bolts, Area, Len, Hello, etc.)
    Object.keys(data).forEach(materialType => {
      const materialData = data[materialType];
      
      // Skip the 'uom' property as it's not a priority
      const priorityKeys = Object.keys(materialData).filter(key => key !== 'uom');
      
      // Iterate through each priority (P3, Priority 100, etc.)
      priorityKeys.forEach(priority => {
        const priorityData = materialData[priority];
        
        // Iterate through each area (area-two, Area, A, etc.)
        Object.keys(priorityData).forEach(area => {
          const areaData = priorityData[area];
          
          // Create a row for this area under the priority
          formattedData.push({
            id: position,
            position: position++,
            materialType: materialType,
            priority: priority,
            materialPriority: `${materialType.toUpperCase()} ${priority.toUpperCase()}`, // Combined key for grouping
            description: area, // Using the area name directly as the description
            grossTotal: areaData['Total / Gross'] || 0,
            fabrication: {
              totalGross: areaData.fabrication?.totalGross || 0,
              percentageCompleted: areaData.fabrication?.percentageCompleted || 0
            },
            qcRelease: {
              totalGross: areaData.qc_release?.totalGross || 0,
              percentageCompleted: areaData.qc_release?.percentageCompleted || 0
            },
            corrisProtDelWrkshp: {
              totalGross: areaData.corrosion_protection?.totalGross || 0,
              percentageCompleted: areaData.corrosion_protection?.percentageCompleted || 0
            },
            delSite: {
              totalGross: areaData.delivery_to_site?.totalGross || 0,
              percentageCompleted: areaData.delivery_to_site?.percentageCompleted || 0
            },
            siteReceipt: {
              totalGross: areaData.site_receipt?.totalGross || 0,
              percentageCompleted: areaData.site_receipt?.percentageCompleted || 0
            },
            installation: {
              totalGross: areaData.installation?.totalGross || 0,
              percentageCompleted: areaData.installation?.percentageCompleted || 0
            }
          });
        });
      });
    });
    
    return formattedData;
  };

  const isBolts = String(materialType || '').toUpperCase() === 'BOLTS';
  const isUnitsUom = String(uom || '').toLowerCase() === 'units';
  const columns = getProgressColumns({
    formatNumber,
    formatPercentage,
    showUomInHeaders: !isUnitsUom,
    uom,
    compactLabels: true,
    isUnitsUom,
    labelsOverride: {
      corrisProtDelWrkshp: isUnitsUom 
        ? 'DEL. WRKSHP.' 
        : 'CORROS. PROT.'
    }
  });

  // Manually override the column title to add line break for UOM
  if (!isUnitsUom) {
    const corrisProtColumn = columns.find(col => col.key === 'corrisProtDelWrkshp');
    if (corrisProtColumn) {
      corrisProtColumn.title = <div style={{ textAlign:'left' }}>CORROS. <br/>PROT. (<span className="uom-text">{String(uom).toLowerCase()}</span>)</div>;
    }
    const siteReceiptCoumn = columns.find(col => col.key === 'siteReceipt');
    if (siteReceiptCoumn) {
      siteReceiptCoumn.title = <div style={{ textAlign: 'left' }}>SITE <br />RECPT. (<span className="uom-text">{String(uom).toLowerCase()}</span>)</div>;
    }
    const totalGrossColumn = columns.find(col => col.key === 'grossTotal');
    if (totalGrossColumn) {
      totalGrossColumn.title = <div style={{ textAlign: 'left' }}>Total / <br />Gross (<span className="uom-text">{String(uom).toLowerCase()}</span>)</div>;
    }
  }
  
  const columnsForDetails = columns.map((col) => {
    let newCol = col.key === 'description' ? { ...col, render: (text) => text } : col;

    // For 'units' type (Bolts), hide the Installation columns (make them blank)
    if (isUnitsUom && (newCol.key === 'installation' || newCol.key === 'installationPercentage')) {
      newCol = {
        ...newCol,
        title: "", // Clear title
        width: '0',
        render: () => null, // Render nothing in cell
      };
    }

    return newCol;
  });
  
  const mergedColumns = columnsForDetails.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        // title: col.title,
        // editing: isEditing(record),
      }),
    };
  });

  // Render priority headers above the table
  const renderPriorityHeaders = () => {
    // Show all material-priority combinations
    return priorities.map((priority, index) => (
      <div 
        key={`priority-header-${index}`} 
        className="priority-header"
        // style={{
        //   backgroundColor: '#f0f0f0',
        //   padding: '12px 16px',
        //   fontWeight: 'bold',
        //   marginBottom: '12px',
        //   borderRadius: '4px',
        //   fontSize: '14px',
        //   boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        // }}
      >
        {priority}
      </div>
    ));
  };
  
  return (
    <Row className="production-manager-parent-row card-container progress-summary-card">
      <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
        <Row align="middle" gutter={[30, 0]}>
          <Col>
            <div>
              <span className="heading-span">{title}</span>
            </div>
          </Col>
        </Row>
      </Col>

      <Col lg={24} xl={24} xxl={24} md={24}>
        <Form
          form={form}
          component={false}
          className="table-style"
        >
          <style>
            {`
              .ant-input {
                padding-left: 12px !important;
              }
              .ant-select-selector {
                padding-left: 12px !important;
              }
              .ant-select-selection-item {
                padding-left: 0px !important;
              }
              /* Target the actual input element inside ant-input */
              input.ant-input {
                text-indent: 8px;
              }
              .special-table {
                width: 100% !important;
              }
              .special-table .ant-table {
                width: 100% !important;
              }
              .special-table .ant-table-container {
                width: 100% !important;
              }
              .special-table .ant-table-content {
                width: 100% !important;
              }
              .special-table table {
                width: 100% !important;
              }
              /* Override card container border radius */
              .production-manager-parent-row.card-container {
                border-radius: 0 !important;
              }
              /* Style for priority sections */
              .priority-header {
                margin-top: 16px;
                margin-bottom: 8px;
                position: relative;
              }
              /* Remove black line below the last row */
              .details-table .ant-table-tbody tr:last-child td {
                border-bottom: none !important;
              }
              /* Style for the total row */
              .total-row td {
                font-weight: bold !important;
                border-top: 1px solid #000 !important;
              }
              /* Set padding to zero for space columns */
              .special-table .ant-table-thead > tr > th:nth-child(5),
              .special-table .ant-table-thead > tr > th:nth-child(8),
              .special-table .ant-table-thead > tr > th:nth-child(11),
              .special-table .ant-table-thead > tr > th:nth-child(14),
              .special-table .ant-table-thead > tr > th:nth-child(17),
              .special-table .ant-table-tbody > tr > td:nth-child(5),
              .special-table .ant-table-tbody > tr > td:nth-child(8),
              .special-table .ant-table-tbody > tr > td:nth-child(11),
              .special-table .ant-table-tbody > tr > td:nth-child(14),
              .special-table .ant-table-tbody > tr > td:nth-child(17),
              .special-table .ant-table-summary > tr > td:nth-child(5),
              .special-table .ant-table-summary > tr > td:nth-child(8),
              .special-table .ant-table-summary > tr > td:nth-child(11),
              .special-table .ant-table-summary > tr > td:nth-child(14),
              .special-table .ant-table-summary > tr > td:nth-child(17) {
                padding: 0 !important;
                width: 0 !important;
                min-width: 0 !important;
                max-width: 0 !important;
              }
              
              /* Override the colored borders from ProgressSummaryReporting component */
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-thead > tr > th:nth-child(7),
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-thead > tr > th:nth-child(10),
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-thead > tr > th:nth-child(13),
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-thead > tr > th:nth-child(16),
              .details-table .ant-table-thead > tr > th:nth-child(18),
              .details-table .ant-table-thead > tr > th:nth-child(19) {
                border-top: none !important;
              }
              
              /* Add top border for fabrication columns with #8d74b1 color */
              .details-table .ant-table-thead > tr > th:nth-child(3),
              .details-table .ant-table-thead > tr > th:nth-child(4) {
                border-top: 1px solid #8d74b1 !important;
              }
              
              /* Add top border for QC Release columns with #ab0800 color */
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-thead > tr > th:nth-child(7) {
                border-top: 1px solid #ab0800 !important;
              }
              
              /* Add top border for CORRIS PROT/ DEL. WRKSHP columns with #ffc000 color */
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-thead > tr > th:nth-child(10) {
                border-top: 1px solid #ffc000 !important;
              }
              
              /* Add top border for DEL SITE columns with #00b0f0 color */
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-thead > tr > th:nth-child(13) {
                border-top: 1px solid #00b0f0 !important;
              }
              
              /* Add top border for SITE RECEIPT columns with #f5a44d color */
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-thead > tr > th:nth-child(16) {
                border-top: 1px solid #f5a44d !important;
              }

              /* Add top border for INSTALLATION columns with #92d050 color */
              .details-table .ant-table-thead > tr > th:nth-child(18),
              .details-table .ant-table-thead > tr > th:nth-child(19) {
                border-top: 1px solid #92d050 !important;
              }
              
              /* Remove default bottom border under the Overall (summary) row */
              .details-table .ant-table-summary > tr > td {
                border-bottom: none !important;
              }

              /* Add bottom border to the total row for QC Release columns */
              .details-table .ant-table-summary > tr > td:nth-child(6),
              .details-table .ant-table-summary > tr > td:nth-child(7) {
                border-bottom: 1px solid #ab0800 !important;
              }
              
              /* Add bottom border to the total row for CORRIS PROT/ DEL. WRKSHP columns */
              .details-table .ant-table-summary > tr > td:nth-child(9),
              .details-table .ant-table-summary > tr > td:nth-child(10) {
                border-bottom: 1px solid #ffc000 !important;
              }
              
              /* Add bottom border to the total row for DEL SITE columns */
              .details-table .ant-table-summary > tr > td:nth-child(12),
              .details-table .ant-table-summary > tr > td:nth-child(13) {
                border-bottom: 1px solid #00b0f0 !important;
              }
              
              /* Add bottom border to the total row for SITE RECEIPT columns */
              .details-table .ant-table-summary > tr > td:nth-child(15),
              .details-table .ant-table-summary > tr > td:nth-child(16) {
                border-bottom: 1px solid #f5a44d !important;
              }
              
              /* Add left border to SITE RECEIPT column */
              .details-table .ant-table-thead > tr > th:nth-child(15),
              .details-table .ant-table-tbody > tr > td:nth-child(15),
              .details-table .ant-table-summary > tr > td:nth-child(15) {
                border-left: 1px solid #f5a44d !important;
              }

              /* Add bottom border to the total row for  INSTALLATION columns */
              .details-table .ant-table-summary > tr > td:nth-child(18),
              .details-table .ant-table-summary > tr > td:nth-child(19) {
                border-bottom: 1px solid #92d050 !important;
              }
              
              /* Add left border to INSTALLATION column */
              .details-table .ant-table-thead > tr > th:nth-child(18),
              .details-table .ant-table-tbody > tr > td:nth-child(18),
              .details-table .ant-table-summary > tr > td:nth-child(18) {
                border-left: 1px solid #92d050 !important;
              }
              
              /* Add left border to DEL SITE column */
              .details-table .ant-table-thead > tr > th:nth-child(12),
              .details-table .ant-table-tbody > tr > td:nth-child(12),
              .details-table .ant-table-summary > tr > td:nth-child(12) {
                border-left: 1px solid #00b0f0 !important;
              }
              
              /* Add left border to CORRIS PROT/ DEL. WRKSHP column */
              .details-table .ant-table-thead > tr > th:nth-child(9),
              .details-table .ant-table-tbody > tr > td:nth-child(9),
              .details-table .ant-table-summary > tr > td:nth-child(9) {
                border-left: 1px solid #ffc000 !important;
              }
              
              /* Add left border to fabrication column */
              .details-table .ant-table-thead > tr > th:nth-child(3),
              .details-table .ant-table-tbody > tr > td:nth-child(3),
              .details-table .ant-table-summary > tr > td:nth-child(3) {
                border-left: 1px solid #8d74b1 !important;
              }
              
              /* Add left border to QC Release column */
              .details-table .ant-table-thead > tr > th:nth-child(6),
              .details-table .ant-table-tbody > tr > td:nth-child(6),
              .details-table .ant-table-summary > tr > td:nth-child(6) {
                border-left: 1px solid #ab0800 !important;
              }
              
              /* Add border radius to top-left corner of fabrication column */
              .details-table .ant-table-thead > tr > th:nth-child(3) {
                border-top-left-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of QC Release column */
              .details-table .ant-table-thead > tr > th:nth-child(6) {
                border-top-left-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of CORRIS PROT/ DEL. WRKSHP column */
              .details-table .ant-table-thead > tr > th:nth-child(9) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add right border to siteReceiptPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(16),
              .details-table .ant-table-tbody > tr > td:nth-child(16),
              .details-table .ant-table-summary > tr > td:nth-child(16) {
                border-right: 1px solid #f5a44d !important;
              }

              /* Add right border to installationPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(19),
              .details-table .ant-table-tbody > tr > td:nth-child(19),
              .details-table .ant-table-summary > tr > td:nth-child(19) {
                border-right: 1px solid #92d050 !important;
              }
              
              /* Add right border to fabricationPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(4),
              .details-table .ant-table-tbody > tr > td:nth-child(4),
              .details-table .ant-table-summary > tr > td:nth-child(4) {
                border-right: 1px solid #8d74b1 !important;
              }
              
              /* Add right border to qcReleasePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(7),
              .details-table .ant-table-tbody > tr > td:nth-child(7),
              .details-table .ant-table-summary > tr > td:nth-child(7) {
                border-right: 1px solid #ab0800 !important;
              }
              
              /* Add right border to corrisProtDelWrkshpPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(10),
              .details-table .ant-table-tbody > tr > td:nth-child(10),
              .details-table .ant-table-summary > tr > td:nth-child(10) {
                border-right: 1px solid #ffc000 !important;
              }
              
              /* Add right border to delSitePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(13),
              .details-table .ant-table-tbody > tr > td:nth-child(13),
              .details-table .ant-table-summary > tr > td:nth-child(13) {
                border-right: 1px solid #00b0f0 !important;
              }
              
              /* Add border radius to top-right corner of fabricationPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(4) {
                border-top-right-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of qcReleasePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(7) {
                border-top-right-radius: 12px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of corrisProtDelWrkshpPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(10) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of DEL SITE column */
              .details-table .ant-table-thead > tr > th:nth-child(12) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of delSitePercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(13) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-left corner of SITE RECEIPT column */
              .details-table .ant-table-thead > tr > th:nth-child(15) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of siteReceiptPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(16) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }

              /* Add border radius to top-left corner of INSTALLATION column */
              .details-table .ant-table-thead > tr > th:nth-child(18) {
                border-top-left-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Add border radius to top-right corner of installationPercentage column */
              .details-table .ant-table-thead > tr > th:nth-child(19) {
                border-top-right-radius: 8px !important;
                overflow: visible !important;
              }
              
              /* Remove bottom borders */
              .details-table .ant-table-tbody tr:last-child td:nth-child(3),
              .details-table .ant-table-tbody tr:last-child td:nth-child(4),
              .details-table .ant-table-tbody tr:last-child td:nth-child(6),
              .details-table .ant-table-tbody tr:last-child td:nth-child(7),
              .details-table .ant-table-tbody tr:last-child td:nth-child(9),
              .details-table .ant-table-tbody tr:last-child td:nth-child(10),
              .details-table .ant-table-tbody tr:last-child td:nth-child(12),
              .details-table .ant-table-tbody tr:last-child td:nth-child(13),
              .details-table .ant-table-tbody tr:last-child td:nth-child(15),
              .details-table .ant-table-tbody tr:last-child td:nth-child(16),
              .details-table .ant-table-tbody tr:last-child td:nth-child(18),
              .details-table .ant-table-tbody tr:last-child td:nth-child(19) {
                border-bottom: none !important;
              }
              .details-table .ant-table-thead > tr > th:nth-child(11),
              .details-table .ant-table-thead > tr > th:nth-child(14) {
                border-radius: 0 !important;
              }
              
              /* Add border radius to bottom corners of fabrication columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(3) {
                border-bottom-left-radius: 12px !important;
                overflow: visible !important;
                border-bottom: 1px solid #8d74b1 !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(4) {
                border-bottom-right-radius: 12px !important;
                overflow: visible !important;
                border-bottom: 1px solid #8d74b1 !important;
              }
              
              /* Add border radius to bottom corners of QC Release columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(6) {
                border-bottom-left-radius: 12px !important;
                overflow: visible !important;
                border-bottom: 1px solid #ab0800 !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(7) {
                border-bottom-right-radius: 12px !important;
                overflow: visible !important;
                border-bottom: 1px solid #ab0800 !important;
              }
              
              /* Add border radius to bottom corners of CORRIS PROT/ DEL. WRKSHP columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(9) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #ffc000 !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(10) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #ffc000 !important;
              }
              
              /* Add border radius to bottom corners of DEL SITE columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(12) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #00b0f0 !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(13) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #00b0f0 !important;
              }
              
              /* Add border radius to bottom corners of SITE RECEIPT columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(15) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #f5a44d !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(16) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #f5a44d !important;
              }

              /* Add border radius to bottom corners of INSTALLATION columns in the summary row */
              .details-table .ant-table-summary > tr > td:nth-child(18) {
                border-bottom-left-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #92d050 !important;
              }
              
              .details-table .ant-table-summary > tr > td:nth-child(19) {
                border-bottom-right-radius: 8px !important;
                overflow: visible !important;
                border-bottom: 1px solid #92d050 !important;
              }
              
              /* Remove border radius */
              .details-table .ant-table-tbody tr:last-child td:nth-child(3),
              .details-table .ant-table-tbody tr:last-child td:nth-child(4),
              .details-table .ant-table-tbody tr:last-child td:nth-child(6),
              .details-table .ant-table-tbody tr:last-child td:nth-child(7),
              .details-table .ant-table-tbody tr:last-child td:nth-child(9),
              .details-table .ant-table-tbody tr:last-child td:nth-child(10),
              .details-table .ant-table-tbody tr:last-child td:nth-child(12),
              .details-table .ant-table-tbody tr:last-child td:nth-child(13),
              .details-table .ant-table-tbody tr:last-child td:nth-child(15),
              .details-table .ant-table-tbody tr:last-child td:nth-child(16),
              .details-table .ant-table-tbody tr:last-child td:nth-child(18),
              .details-table .ant-table-tbody tr:last-child td:nth-child(19) {
                border-radius: 0 !important;
              }
              ${isUnitsUom && `
              /* Hide borders for Installation columns (18 and 19) when isUnitsUom is true */
              .special-table.hide-installation-borders .ant-table-thead > tr > th:nth-child(18),
              .special-table.hide-installation-borders .ant-table-thead > tr > th:nth-child(19),
              .special-table.hide-installation-borders .ant-table-tbody > tr > td:nth-child(18),
              .special-table.hide-installation-borders .ant-table-tbody > tr > td:nth-child(19),
              .special-table.hide-installation-borders .ant-table-summary > tr > td:nth-child(18),
              .special-table.hide-installation-borders .ant-table-summary > tr > td:nth-child(19) {
                // border: none !important;
                border-top: none !important;
                border-bottom: none !important;
                border-left: none !important;
                border-right: none !important;
              }
              .special-table.hide-installation-borders .ant-table-tbody > tr > td:nth-child(18).ant-table-cell.ps-col.ps-col-stage,
              .special-table.hide-installation-borders .ant-table-tbody > tr > td:nth-child(19).ant-table-cell.ps-col.ps-col-stage {
                border-bottom: 1px solid black !important;
                border-top: 1px solid black !important;
              }
            `}
          `}
          </style>
          {/* Render a separate table for each material-priority combination */}
          {priorities.map((priority, index) => {
            // Filter data for this specific priority
            const priorityData = data.filter(item => item.materialPriority === priority);
            
            // Calculate totals for this priority
            const priorityTotal = {
              grossTotal: 0,
              fabrication: { totalGross: 0, percentageCompleted: 0 },
              qcRelease: { totalGross: 0, percentageCompleted: 0 },
              corrisProtDelWrkshp: { totalGross: 0, percentageCompleted: 0 },
              delSite: { totalGross: 0, percentageCompleted: 0 },
              siteReceipt: { totalGross: 0, percentageCompleted: 0 },
              installation: { totalGross: 0, percentageCompleted: 0 }
            };
            
            // Calculate totals for this priority
            priorityData.forEach(item => {
              priorityTotal.grossTotal += Number(item.grossTotal) || 0;
              priorityTotal.fabrication.totalGross += Number(item.fabrication?.totalGross) || 0;
              priorityTotal.qcRelease.totalGross += Number(item.qcRelease?.totalGross) || 0;
              priorityTotal.corrisProtDelWrkshp.totalGross += Number(item.corrisProtDelWrkshp?.totalGross) || 0;
              priorityTotal.delSite.totalGross += Number(item.delSite?.totalGross) || 0;
              priorityTotal.siteReceipt.totalGross += Number(item.siteReceipt?.totalGross) || 0;
              priorityTotal.installation.totalGross += Number(item.installation?.totalGross) || 0;
            });
            
            // Calculate percentages as average of all individual percentages for this priority
            const priorityCount = priorityData.length;
            if (priorityCount > 0) {
              let fabricationPercentageSum = 0;
              let qcReleasePercentageSum = 0;
              let corrisProtDelWrkshpPercentageSum = 0;
              let delSitePercentageSum = 0;
              let siteReceiptPercentageSum = 0;
              let installationPercentageSum = 0;
              
              priorityData.forEach(item => {
                fabricationPercentageSum += Number(item.fabrication?.percentageCompleted) || 0;
                qcReleasePercentageSum += Number(item.qcRelease?.percentageCompleted) || 0;
                corrisProtDelWrkshpPercentageSum += Number(item.corrisProtDelWrkshp?.percentageCompleted) || 0;
                delSitePercentageSum += Number(item.delSite?.percentageCompleted) || 0;
                siteReceiptPercentageSum += Number(item.siteReceipt?.percentageCompleted) || 0;
                installationPercentageSum += Number(item.installation?.percentageCompleted) || 0;
              });
              
              priorityTotal.fabrication.percentageCompleted = fabricationPercentageSum / priorityCount;
              priorityTotal.qcRelease.percentageCompleted = qcReleasePercentageSum / priorityCount;
              priorityTotal.corrisProtDelWrkshp.percentageCompleted = corrisProtDelWrkshpPercentageSum / priorityCount;
              priorityTotal.delSite.percentageCompleted = delSitePercentageSum / priorityCount;
              priorityTotal.siteReceipt.percentageCompleted = siteReceiptPercentageSum / priorityCount;
              priorityTotal.installation.percentageCompleted = installationPercentageSum / priorityCount;
            }
            
            return (
              <div key={`priority-section-${index}`} style={{ marginBottom: '30px' }}>
                <div 
                  className="priority-header"
                  style={{
                    backgroundColor: '#f0f0f0',
                    padding: '12px 16px',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {priority}
                </div>
                <Table
                  className={`special-table activity-type-table project-statuses-table details-table ${isUnitsUom ? 'hide-installation-borders' : ''}`}
                  rowClassName={() => 'editable-row'}
                  dataSource={priorityData}
                  loading={loading}
                  columns={mergedColumns}
                  pagination={false}
                  rowKey="position"
                  style={{ width: '100%' }}
                  summary={() => {
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row className="total-row">
                          <Table.Summary.Cell index={0} align="left">
                            OVERALL {priority.split(' ').slice(-1).join(' ')}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="center">
                            {formatNumber(priorityTotal.grossTotal)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                            {formatNumber(priorityTotal.fabrication.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="center">
                            {formatPercentage(priorityTotal.fabrication.percentageCompleted)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4}>
                            {/* Empty cell for space column */}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="center">
                            {formatNumber(priorityTotal.qcRelease.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="center">
                            {formatPercentage(priorityTotal.qcRelease.percentageCompleted)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7}>
                            {/* Empty cell for space column */}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8} align="center">
                            {formatNumber(priorityTotal.corrisProtDelWrkshp.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={9} align="center">
                            {formatPercentage(priorityTotal.corrisProtDelWrkshp.percentageCompleted)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={10}>
                            {/* Empty cell for space column */}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={11} align="center">
                            {formatNumber(priorityTotal.delSite.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={12} align="center">
                            {formatPercentage(priorityTotal.delSite.percentageCompleted)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={13}>
                            {/* Empty cell for space column */}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={14} align="center">
                            {formatNumber(priorityTotal.siteReceipt.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={15} align="center">
                            {formatPercentage(priorityTotal.siteReceipt.percentageCompleted)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={16}>
                            {/* Empty cell for space column */}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={17} align="center">
                            {isUnitsUom ? null : formatNumber(priorityTotal.installation.totalGross)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={18} align="center">
                            {isUnitsUom ? null : formatPercentage(priorityTotal.installation.percentageCompleted)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            );
          })}
          <Col lg={24} xl={24} xxl={24} md={24} className="table">
            {canUpdate && (
              <Button
                icon={<PlusOutlined />}
                className="add-user-btn"
                size="middle"
                // disabled={isAdding}
                style={{
                  marginTop: "20px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                TYPE
              </Button>
            )}
          </Col>
        </Form>
      </Col>
    </Row>
  );
};

export default ProgressSummaryReportingDetails;